import { useEffect, useRef, useState } from 'react'
import { Ban, ChevronDown } from 'lucide-react'
import { ICON_MAP } from './stylePresets'

type IconPickerProps = {
  value?: string
  onChange: (name: string | undefined) => void
}

/**
 * Sélecteur d'icône : déclencheur compact affichant l'icône courante, popover au clic
 * pour la grille des 16 icônes + « Aucune ». Décision validée sur maquette, 2026-08-06 —
 * l'icône est un réglage rare, pas besoin d'occuper ~120px en permanence.
 */
export default function IconPicker({ value, onChange }: IconPickerProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const names = Object.keys(ICON_MAP)
  const Current = value ? ICON_MAP[value] : null

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  return (
    <div ref={rootRef} className="relative flex min-h-[28px] items-center justify-between gap-2">
      <span className="text-xs text-[#5F6477]">Icône</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Icône : ${value ?? 'aucune'}`}
        className="flex h-7 w-[152px] shrink-0 items-center gap-1.5 rounded-md border border-[#E2E4ED] bg-white px-2 text-xs text-[#1B2559]"
      >
        {Current ? <Current size={13} /> : <Ban size={13} className="text-[#9196A8]" />}
        <span className="truncate">{value ?? 'Aucune'}</span>
        <ChevronDown size={12} className="ml-auto shrink-0 text-[#9196A8]" />
      </button>

      {open && (
        <div className="absolute right-0 top-8 z-10 grid w-60 grid-cols-6 gap-1.5 rounded-lg border border-[#E2E4ED] bg-white p-2 shadow-lg">
          <button
            type="button"
            aria-label="Aucune"
            aria-pressed={value === undefined}
            onClick={() => {
              onChange(undefined)
              setOpen(false)
            }}
            className="flex h-8 w-8 items-center justify-center rounded-lg"
            style={{
              border: value === undefined ? '2px solid #6366F1' : '1px solid #E2E4ED',
              backgroundColor: value === undefined ? '#F4F5FA' : '#ffffff',
            }}
          >
            <Ban size={14} color={value === undefined ? '#6366F1' : '#8B8FA7'} />
          </button>
          {names.map((name) => {
            const Icon = ICON_MAP[name]
            const active = value === name
            return (
              <button
                key={name}
                type="button"
                aria-label={name}
                aria-pressed={active}
                onClick={() => {
                  onChange(name)
                  setOpen(false)
                }}
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{
                  border: active ? '2px solid #6366F1' : '1px solid #E2E4ED',
                  backgroundColor: active ? '#F4F5FA' : '#ffffff',
                }}
              >
                <Icon size={14} color={active ? '#6366F1' : '#1B2559'} />
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
